/**
 * Asynchronous layer builds for one game (priori_stack_v1.build_run).
 *
 * A build runs in a worker, never in the request, so the page queues a
 * batch (POST .../builds), then polls GET .../builds while anything is
 * queued or running. One provider per match page: the Pipeline panel and
 * the summary card's Build next/all share its state, its single poller and
 * its cancel. Every status change is written to the Action log; a
 * succeeded step logs 'done', which refreshes the rest of the page.
 * Reloading the page picks a running batch back up from the server.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState,
         type ReactNode } from 'react'

import { ApiError, api } from '../lib/api'
import { useActionLog } from './actionLog'

export type RunStatus =
  'queued' | 'running' | 'cancelling' | 'succeeded' | 'failed' | 'skipped'
  | 'cancelled'

export type BuildRun = {
  run_id: number
  batch_id: string
  position: number
  step: string
  status: RunStatus
  runner: string | null
  error: string | null
  summary: Record<string, unknown> | null
  queued_at: string
  started_at: string | null
  finished_at: string | null
  duration_ms: number | null
  /** queued with Force: may overwrite the step's existing outputs */
  force?: boolean
}

export type StepStat = {
  step: string; runner: string | null; n: number
  median_ms: number; p90_ms: number; max_ms: number; last_ms: number
}

type BuildRuns = {
  runs: BuildRun[]
  /** the batch running now (or next in line), if any */
  activeBatch: string | null
  /** every batch still queued/running/cancelling, in queue order */
  activeBatches: string[]
  /** steps queued or running in any active batch — build them again and
   *  they would only be queued twice */
  activeSteps: Set<string>
  /** step currently running, if any */
  runningStep: string | null
  /** latest run per step (newest first order preserved) */
  latest: Map<string, BuildRun>
  /** build-time statistics, per step (the runner with the most runs) */
  stats: Map<string, StepStat>
  /** force: overwrite the steps' existing outputs (where allowed) */
  /** large: run the batch on the 24 GiB large job, not the 8 GiB worker */
  start: (steps: string[], what: string, force?: boolean, large?: boolean) => Promise<void>
  /** cancel one batch; the running one when no id is given */
  cancel: (batchId?: string) => Promise<void>
  /** reload the game's runs now — after anything that queued a batch
   *  outside start() (a Game Facts change's rebuild) */
  refresh: () => Promise<void>
}

const BuildRunsContext = createContext<BuildRuns | null>(null)

// The run statuses the Action Log last saw, per game, for this tab: the
// diff on return is what finished while the operator was elsewhere.
function seenKey(gameId: number) {
  return `layerstack.buildseen.${gameId}`
}

function readSeen(gameId: number): Map<number, RunStatus> | null {
  try {
    const v = sessionStorage.getItem(seenKey(gameId))
    if (v == null) return null
    return new Map(Object.entries(JSON.parse(v) as Record<string, RunStatus>)
      .map(([k, s]) => [Number(k), s]))
  } catch { return null }
}

function writeSeen(gameId: number, seen: Map<number, RunStatus>) {
  try {
    sessionStorage.setItem(seenKey(gameId),
                           JSON.stringify(Object.fromEntries(seen)))
  } catch { /* quota or private mode: this visit only */ }
}

/** null outside a provider: callers hide their build controls. */
export function useBuildRuns(): BuildRuns | null {
  return useContext(BuildRunsContext)
}

// 'cancelling' is active: the game stays locked until the worker stops.
const ACTIVE: RunStatus[] = ['queued', 'running', 'cancelling']
const POLL_MS = 3000

/** Live " · 1m 12s" counter since an ISO timestamp. */
export function Elapsed({ since }: { since: string }) {
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])
  return <> · {secs(now - new Date(since).getTime())}</>
}

/** What a queued step is waiting on, in words. Cloud Run takes minutes to
 *  start a job's container on dev (4m02s between execution start and our
 *  process, measured 2026-09-29) — say so rather than look stuck. */
export function waitingFor(runner: string | null): string {
  return runner === 'cloudrun'
    ? 'waiting for Cloud Run to start the worker (usually 4–5 min)'
    : runner === 'worker'
      ? 'waiting for the build worker to pick it up'
      : 'starting the worker'
}

export function secs(ms: number | null | undefined): string {
  if (ms == null) return '—'
  return ms < 60000 ? `${(ms / 1000).toFixed(1)}s`
    : `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`
}

/** A builder summary as a compact one-liner. */
export function formatSummary(s: Record<string, unknown>): string {
  const parts: string[] = []
  // Unwrap {builder: {...}, materialized_bytes, published: [...]}
  const builder = (typeof s.builder === 'object' && s.builder !== null
    ? s.builder : s) as Record<string, unknown>
  for (const k of Object.keys(builder)) {
    const v = builder[k]
    if (typeof v === 'number') parts.push(`${k}: ${v.toLocaleString()}`)
    else if (typeof v === 'string' && v.length < 60) parts.push(`${k}: ${v}`)
  }
  if (typeof s.materialized_bytes === 'number')
    parts.push(`fetched: ${(s.materialized_bytes / 1048576).toFixed(1)} MB`)
  if (Array.isArray(s.published))
    parts.push(`published: ${s.published.length} file${s.published.length === 1 ? '' : 's'}`)
  return parts.length > 0 ? `→ ${parts.join(' · ')}` : ''
}

export function BuildRunsProvider({ gameId, children }: {
  gameId: number; children: ReactNode
}) {
  const { log } = useActionLog()
  const [runs, setRuns] = useState<BuildRun[]>([])
  const [stats, setStats] = useState<Map<string, StepStat>>(new Map())
  // Last status seen per run: the diff is what gets logged.
  const seen = useRef<Map<number, RunStatus> | null>(null)

  const loadStats = useCallback(() => {
    api<{ steps: StepStat[] }>('/api/v1/opsqa/build-stats')
      .then((d) => {
        const m = new Map<string, StepStat>()
        for (const s of d.steps) {
          const cur = m.get(s.step)
          if (!cur || s.n > cur.n) m.set(s.step, s)
        }
        setStats(m)
      })
      .catch(() => {})
  }, [])

  const logTransitions = useCallback((next: BuildRun[]) => {
    // First load: what the log last saw of THIS game (while the operator
    // was on another one, steps finished that were never logged), else
    // nothing — a game first opened in this tab logs no history.
    const prev = seen.current ?? readSeen(gameId)
    const now = new Map(next.map((r) => [r.run_id, r.status]))
    seen.current = now
    writeSeen(gameId, now)
    if (prev === null) {
      // First load: report a batch already in flight, not history.
      const running = next.find((r) => r.status === 'running')
      if (running) log('info', `▸ Watching a running build: ${running.step}`)
      return
    }
    // Oldest first so the log reads in order.
    for (const r of [...next].reverse()) {
      const was = prev.get(r.run_id)
      if (was === r.status) continue
      if (r.status === 'running') log('start', `  ${r.step}…`)
      else if (r.status === 'succeeded') {
        const extra = r.summary ? ` ${formatSummary(r.summary)}` : ''
        log('done', `  ${r.step}: done (${secs(r.duration_ms)})${extra}`)
        const over = r.summary?.overwritten
        if (Array.isArray(over) && over.length > 0)
          log('info', `  ${r.step}: overwrote ${over.length} existing object${over.length === 1 ? '' : 's'}`)
      } else if (r.status === 'failed')
        log('fail', `  ${r.step}: FAILED — ${r.error ?? 'unknown error'}`)
      else if (r.status === 'cancelling') log('info', `  ${r.step}: cancelling — waiting for the worker to stop`)
      else if (r.status === 'cancelled') log('fail', `  ${r.step}: cancelled`)
      else if (r.status === 'skipped') log('info', `  ${r.step}: skipped`)
    }
  }, [gameId, log])

  const load = useCallback(async () => {
    try {
      const d = await api<{ runs: BuildRun[] }>(
        `/api/v1/opsqa/games/${gameId}/builds`)
      logTransitions(d.runs)
      setRuns(d.runs)
      return d.runs
    } catch {
      return null
    }
  }, [gameId, logTransitions])

  useEffect(() => {
    seen.current = null
    setRuns([])
    void load()
    loadStats()
  }, [load, loadStats])

  const active = runs.filter((r) => ACTIVE.includes(r.status))
  // Queue order: smallest run_id first (the API lists newest first).
  const activeBatches = [...new Set(
    [...active].sort((a, b) => a.run_id - b.run_id).map((r) => r.batch_id))]
  const runningBatch = active.find(
    (r) => r.status === 'running' || r.status === 'cancelling')?.batch_id
  const activeBatch = runningBatch ?? activeBatches[0] ?? null
  const activeSteps = new Set(active.map((r) => r.step))

  // Poll only while something is queued or running.
  useEffect(() => {
    if (!activeBatch) return
    const t = setInterval(() => {
      void load().then((r) => {
        if (r && !r.some((x) => ACTIVE.includes(x.status))) {
          log('info', '▸ Batch finished')
          loadStats()
        }
      })
    }, POLL_MS)
    return () => clearInterval(t)
  }, [activeBatch, load, loadStats, log])

  const start = useCallback(async (steps: string[], what: string,
                                  force = false, large = false) => {
    if (steps.length === 0) { log('info', 'Nothing to build'); return }
    log('info', `▸ Queued ${steps.length} step${steps.length === 1 ? '' : 's'} ${what}${force ? ' (FORCE: overwrite existing)' : ''}${large ? ' on the large job (24 GiB)' : ''}`)
    try {
      // What the batch waits on shows on its step cards (waitingFor), not
      // as an Action Log line: it said nothing the card did not.
      await api(`/api/v1/opsqa/games/${gameId}/builds`,
                { method: 'POST', body: JSON.stringify(large ? { steps, force, large } : { steps, force }) })
      await load()
    } catch (e: unknown) {
      log('fail', `Build not started — ${e instanceof ApiError ? e.message : String(e)}`)
      void load()
    }
  }, [gameId, load, log])

  const cancel = useCallback(async (batchId?: string) => {
    const target = batchId ?? activeBatch
    if (!target) return
    try {
      const r = await api<{ stopped: boolean; detail: string }>(
        `/api/v1/opsqa/builds/${target}/cancel`, { method: 'POST' })
      log('info', `▸ Cancel requested — ${r.detail}`)
    } catch (e: unknown) {
      log('fail', `Cancel failed — ${e instanceof ApiError ? e.message : String(e)}`)
    }
    void load()
  }, [activeBatch, load, log])

  const latest = new Map<string, BuildRun>()
  for (const r of runs) if (!latest.has(r.step)) latest.set(r.step, r)

  return (
    <BuildRunsContext.Provider value={{
      runs, activeBatch, activeBatches, activeSteps,
      runningStep: runs.find((r) => r.status === 'running')?.step ?? null,
      latest, stats, start, cancel,
      refresh: async () => { await load() },
    }}>
      {children}
    </BuildRunsContext.Provider>
  )
}

// ── What the build workers are doing, across ALL games ─────────────────

export type ActiveRun = BuildRun & { game_id: number }

/** Every queued/running build on any game, refreshed every 5 s while the
 *  tab is visible — so an admin on game 16 can see the worker is busy with
 *  game 15, and how much is queued where. */
export function useWorkerActivity(): ActiveRun[] | null {
  const [runs, setRuns] = useState<ActiveRun[] | null>(null)
  useEffect(() => {
    let alive = true
    const load = () => {
      if (document.visibilityState === 'hidden') return
      api<{ runs: ActiveRun[] }>('/api/v1/opsqa/builds/active')
        .then((d) => { if (alive) setRuns(d.runs) })
        .catch(() => {})
    }
    load()
    const t = setInterval(load, 5000)
    return () => { alive = false; clearInterval(t) }
  }, [])
  return runs
}

export function WorkerActivity({ gameId }: { gameId: number }) {
  const runs = useWorkerActivity()
  if (runs === null) return null
  const running = runs.filter(
    (r) => r.status === 'running' || r.status === 'cancelling')
  const queuedByGame = new Map<number, Set<string>>()
  for (const r of runs) {
    if (r.status !== 'queued') continue
    const set = queuedByGame.get(r.game_id) ?? new Set<string>()
    set.add(r.batch_id)
    queuedByGame.set(r.game_id, set)
  }
  const tag = (g: number) => (g === gameId ? `game ${g} (this game)` : `game ${g}`)
  return (
    <div data-testid="worker-activity"
         style={{ fontSize: '11px', marginBottom: 'var(--space-2)',
                  padding: '4px 8px', borderRadius: '4px',
                  border: '1px solid #333', color: '#bbb' }}>
      <strong style={{ color: '#ddd' }}>Build worker:</strong>{' '}
      {running.length === 0
        ? <span>idle</span>
        : running.map((r, i) => (
          <span key={r.run_id}
                style={{ color: r.game_id === gameId ? '#eab308' : '#ddd' }}>
            {i > 0 && ' · '}
            {r.status === 'cancelling' ? 'cancelling' : 'running'}{' '}
            {tag(r.game_id)}: {r.step}
            {r.started_at && <Elapsed since={r.started_at} />}
          </span>
        ))}
      {queuedByGame.size > 0 && (
        <span style={{ color: '#888' }}>
          {'  ·  queued: '}
          {[...queuedByGame.entries()].map(([g, b]) =>
            `${tag(g)} (${b.size} batch${b.size === 1 ? '' : 'es'})`).join(', ')}
        </span>
      )}
    </div>
  )
}

